import { sleep } from '@/businessRepository/reminderTable/test/testUtils';
import { IBusiness, getKeyType, isKeyOfIBusiness } from '@/coreDomainsModule/entities/businessBase';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 * テーブルのヘッダーを表す型
 * @property headerKey - ヘッダーのキー
 * @property headerName - ヘッダーの名前
 */
export type TableContentHeaders = {
  headerKey: string;
  headerName: string;
};

/**
 * IBusinessから直接取れない型をまとめたもの
 */
export const additionalHeaders: TableContentHeaders[] = [
  {
    headerKey: 'customerAnnounce-1',
    headerName: 'N月内示',
  },
  {
    headerKey: 'customerAnnounce-2',
    headerName: 'N+1月内示',
  },
  {
    headerKey: 'customerAnnounce-3',
    headerName: 'N+2月内示',
  },
  {
    headerKey: 'customerAnnounce-4',
    headerName: 'N+3月内示',
  },
  {
    headerKey: 'arrivals-1',
    headerName: 'N月入荷',
  },
  {
    headerKey: 'arrivals-2',
    headerName: 'N-1月入荷',
  },
  {
    headerKey: 'arrivals-3',
    headerName: 'N-2月入荷',
  },
  {
    headerKey: 'arrivals-4',
    headerName: 'N-3月入荷',
  },
  {
    headerKey: 'salesResults-1',
    headerName: 'N月販売実績',
  },
  {
    headerKey: 'salesResults-2',
    headerName: 'N-1月販売実績',
  },
  {
    headerKey: 'salesResults-3',
    headerName: 'N-2月販売実績',
  },
  {
    headerKey: 'salesResults-4',
    headerName: 'N-3月販売実績',
  },
  {
    headerKey: 'mvpInventory-1',
    headerName: 'N-1月在庫',
  },
  {
    headerKey: 'mvpInventory-2',
    headerName: 'N-2月在庫',
  },
  {
    headerKey: 'mvpInventory-3',
    headerName: 'N-3月在庫',
  },
];

/**
 * 追加のヘッダーのキーを元に、その値を取得する
 * いろいろ無理があるので、全てに対してmapを作成している
 * @param headerKey - ヘッダーのキー
 * @param item - 取引情報
 * @returns 計算結果
 */
export const getValueForAdditionalHeaders = (headerKey: string, item: IBusiness): Result<number, Error> => {
  const nowDate = new Date();
  const nowYear = nowDate.getFullYear();
  const nowMonth = nowDate.getMonth() + 1;

  let targetYearMonth = { updateYear: nowYear, updateMonth: nowMonth };

  if (headerKey.indexOf('customerAnnounce') !== -1) {
    if (headerKey === 'customerAnnounce-1') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, 0);
    }
    if (headerKey === 'customerAnnounce-2') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, 1);
    }
    if (headerKey === 'customerAnnounce-3') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, 2);
    }
    if (headerKey === 'customerAnnounce-4') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, 3);
    }
    const customerAnnounce = item.customerAnnounce?.find((arr) =>
      DateService.equalYearMonthInfo(arr, { year: targetYearMonth.updateYear, month: targetYearMonth.updateMonth })
    );
    if (customerAnnounce) {
      return {
        type: 'success',
        value: customerAnnounce.amount,
      };
    } else {
      return {
        type: 'error',
        error: new Error('該当する情報がありません'),
      };
    }
  }

  if (headerKey.indexOf('arrivals') !== -1) {
    if (headerKey === 'arrivals-1') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, 0);
    }
    if (headerKey === 'arrivals-2') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -1);
    }
    if (headerKey === 'arrivals-3') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -2);
    }
    if (headerKey === 'arrivals-4') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -3);
    }
    const arrivals = item.arrivals?.find((arr) =>
      DateService.equalYearMonthInfo(arr, { year: targetYearMonth.updateYear, month: targetYearMonth.updateMonth })
    );
    if (arrivals) {
      return {
        type: 'success',
        value: arrivals.amount,
      };
    } else {
      return {
        type: 'error',
        error: new Error('該当する情報がありません'),
      };
    }
  }

  if (headerKey.indexOf('salesResults') !== -1) {
    if (headerKey === 'salesResults-1') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, 0);
    }
    if (headerKey === 'salesResults-2') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -1);
    }
    if (headerKey === 'salesResults-3') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -2);
    }
    if (headerKey === 'salesResults-4') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -3);
    }
    const salesResults = item.salesResults?.find((arr) =>
      DateService.equalYearMonthInfo(arr, { year: targetYearMonth.updateYear, month: targetYearMonth.updateMonth })
    );
    if (salesResults) {
      return {
        type: 'success',
        value: salesResults.amount,
      };
    } else {
      return {
        type: 'error',
        error: new Error('該当する情報がありません'),
      };
    }
  }

  if (headerKey.indexOf('mvpInventory') !== -1) {
    if (headerKey === 'mvpInventory-1') {
      // targetYearMonthから１ヶ月前の年月を取得
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -1);
    }
    if (headerKey === 'mvpInventory-2') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -2);
    }
    if (headerKey === 'mvpInventory-3') {
      targetYearMonth = DateService.updateYearMonth(nowYear, nowMonth, -3);
    }
    const mvpInventory = item.mvpInventory?.find((arr) =>
      DateService.equalYearMonthInfo(arr, { year: targetYearMonth.updateYear, month: targetYearMonth.updateMonth })
    );
    if (mvpInventory) {
      return {
        type: 'success',
        value: mvpInventory.amount,
      };
    } else {
      return {
        type: 'error',
        error: new Error('該当する情報がありません'),
      };
    }
  }

  return {
    type: 'error',
    error: new Error('該当する情報がありません'),
  };
};

/**
 * 型の情報を参照して、number型の値のみを追加する
 * @param headerKey - ヘッダーのキー
 * @param headerName - ヘッダーの名前
 */
export const addTableContentHeader = (headerKey: string, headerName: string): Result<TableContentHeaders, Error> => {
  const key = headerKey;

  /**
   * keyOfIBusinessが存在する場合、そのキーに対応する型を取得
   */
  if (isKeyOfIBusiness(key)) {
    const keyType: IBusiness[typeof key] = getKeyType(key);
    if (typeof keyType === 'number') {
      return {
        type: 'success',
        value: {
          headerKey,
          headerName,
        },
      };
    } else {
      return {
        type: 'error',
        error: new Error('計算可能な値ではありません'),
      };
    }
  } else {
    return {
      type: 'error',
      error: new Error('keyがIBusinessのキーとして存在しません'),
    };
  }
};
